import { useEffect } from 'react';

interface LandingPageOptimizerProps {
  enableCriticalResourceHints?: boolean;
  enableImageOptimization?: boolean;
  enableFontOptimization?: boolean;
  enableJavaScriptOptimization?: boolean;
}

export const LandingPageOptimizer = ({
  enableCriticalResourceHints = true,
  enableImageOptimization = true,
  enableFontOptimization = true,
  enableJavaScriptOptimization = true
}: LandingPageOptimizerProps) => {
  
  useEffect(() => {
    if (enableCriticalResourceHints) {
      addCriticalResourceHints();
    }
    
    if (enableImageOptimization) {
      optimizeImages();
    }
    
    if (enableFontOptimization) {
      optimizeFonts();
    }
    
    if (enableJavaScriptOptimization) {
      optimizeJavaScript();
    }
  }, [enableCriticalResourceHints, enableImageOptimization, enableFontOptimization, enableJavaScriptOptimization]);

  return null;
};

// Add critical resource hints for better performance
const addCriticalResourceHints = () => {
  const head = document.head;
  
  // Preconnect to critical domains
  const preconnectDomains = [
    'https://www.googletagmanager.com',
    'https://www.google-analytics.com',
    'https://pagead2.googlesyndication.com',
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ];
  
  preconnectDomains.forEach(domain => {
    if (!document.querySelector(`link[href="${domain}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      if (domain.includes('fonts.gstatic.com')) {
        link.crossOrigin = 'anonymous';
      }
      head.appendChild(link);
    }
  });

  // Preload critical CSS
  const criticalCSS = document.querySelector('style[data-vite-dev-id]');
  if (criticalCSS) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = 'data:text/css;base64,' + btoa(criticalCSS.textContent || '');
    head.appendChild(link);
  }
};

// Optimize images for better performance
const optimizeImages = () => {
  const images = document.querySelectorAll('img');
  
  images.forEach((img) => {
    // Add loading="lazy" to non-critical images
    if (!img.hasAttribute('loading') && !img.closest('[data-critical]')) {
      img.setAttribute('loading', 'lazy');
    }
    
    // Add decoding="async" for better performance
    if (!img.hasAttribute('decoding')) {
      img.setAttribute('decoding', 'async');
    }
    
    // Ensure alt attributes exist for accessibility
    if (!img.hasAttribute('alt')) {
      img.setAttribute('alt', '');
    }
  });
};

// Optimize font loading
const optimizeFonts = () => {
  // Font display swap for better loading experience
  const fontFaceCSS = `
    @font-face {
      font-family: 'Fredoka';
      font-display: swap;
      src: local('Fredoka');
    }
    @font-face {
      font-family: 'Inter';
      font-display: swap;
      src: local('Inter');
    }
  `;
  
  if (!document.querySelector('#font-optimization')) {
    const style = document.createElement('style');
    style.id = 'font-optimization';
    style.textContent = fontFaceCSS;
    document.head.appendChild(style);
  }
};

// Optimize JavaScript execution
const optimizeJavaScript = () => {
  // Defer non-critical scripts
  const scripts = document.querySelectorAll('script[src]');
  
  scripts.forEach((script) => {
    const src = script.getAttribute('src');
    
    // Add defer to non-critical scripts
    if (src && 
        !src.includes('gtag') && 
        !src.includes('adsbygoogle') &&
        !script.hasAttribute('defer') &&
        !script.hasAttribute('async')) {
      script.setAttribute('defer', 'true');
    }
  });
  
  // Preload critical JavaScript modules
  const criticalModules = ['/src/main.tsx'];
  
  criticalModules.forEach(module => {
    if (!document.querySelector(`link[href="${module}"]`)) {
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = module;
      document.head.appendChild(link);
    }
  });
};

// Performance monitoring
export const monitorLandingPagePerformance = () => {
  if ('performance' in window) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        const metrics = {
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
          loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
          firstPaint: 0,
          firstContentfulPaint: 0
        };
        
        // Get paint timings
        const paintTimings = performance.getEntriesByType('paint');
        paintTimings.forEach((entry) => {
          if (entry.name === 'first-paint') {
            metrics.firstPaint = entry.startTime;
          } else if (entry.name === 'first-contentful-paint') {
            metrics.firstContentfulPaint = entry.startTime;
          }
        });
        
        // Send to Google Analytics
        if (window.gtag) {
          window.gtag('event', 'page_performance', {
            dom_content_loaded: metrics.domContentLoaded,
            load_complete: metrics.loadComplete,
            first_paint: metrics.firstPaint,
            first_contentful_paint: metrics.firstContentfulPaint
          });
        }
        
        console.log('Landing page performance metrics:', metrics);
      }, 0);
    });
  }
};